import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { graphql, useApolloClient } from 'react-apollo';
import qs from 'query-string';
import { Formik } from 'formik';

import OnboardingLayout from '../../../apps/temp/views/onboarding/OnboardingLayout/OnboardingLayout';
import { getErrorString } from '../../../util/normalize-errors';
import { accountLogin as accountLoginMutation } from '../../../graphql/mutations/account';
import { AlbumFolderWithCreatorBrandDetails } from '../../../graphql/queries/album-folder';
import { accountEmailExists } from '../../../graphql/queries/account';
import EmailPasswordForm from './components/EmailPasswordForm';
import PhoneForm from './components/PhoneForm';
import PinVerifyForm from './components/PinVerifyForm';
import EmailForm from './components/EmailForm';
import EmailPinVerifyForm from './components/EmailPinVerifyForm';
import Alert from '../../../components/global/Alert';
import WaldoSpeechBubble from '../../../components/global/WaldoSpeechBubble';
import Button from '../../../components/global/Button';
import { loginSuccess } from '../../../actions/auth-actions';
import usePhoneAuth from '../../../components/auth/hooks/usePhoneAuth';
import useGoogleRecaptcha from '../../../services/recaptcha/useGoogleRecaptcha';
import { parsePhone } from '../../../util/phone';
import withGoogleRecaptchaProvider from '../../../components/auth/hocs/withGoogleRecaptchaProvider';
import useEmailPinAuth from '../../../components/auth/hooks/useEmailPinAuth';
import Angle from '../../../components/global/Angle';

const propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }),
  accountLogin: PropTypes.func.isRequired,
};

const AUTH_STEPS = {
  PHONE: 'PHONE',
  PIN_VERIFY: 'PIN_VERIFY',
  EMAIL: 'EMAIL',
  EMAIL_PASSWORD: 'EMAIL_PASSWORD',
  EMAIL_PIN_VERIFY: 'EMAIL_PIN_VERIFY',
};

const PhoneAuthView = ({ location, history, accountLogin, albumFolder }) => {
  const [step, setStep] = useState(AUTH_STEPS.PHONE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useDispatch();
  const client = useApolloClient();
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();
  const { onLoginByEmail, onEmailAuthenticationRequest } = useEmailPinAuth();

  const { next = '/app' } = qs.parse(location?.search);

  useEffect(() => {
    if (pinSent && step !== AUTH_STEPS.PIN_VERIFY) {
      setStep(AUTH_STEPS.PIN_VERIFY);
    }
  }, [pinSent]);

  const attemptEmailPasswordLogin = async ({ email, password }) => {
    setLoading(true);
    setError('');

    try {
      await accountLogin(email, password);
      dispatch(loginSuccess());
      history.replace(next);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPhoneVerificationRequest = async ({
    phone_number: phoneNumber,
    force_allow_action: forceAllowAction,
  }) => {
    setLoading(true);
    setError('');

    try {
      const token = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(phoneNumber, true, token, forceAllowAction);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPhoneLogin = async ({ phone_number: phoneNumber, code }) => {
    setLoading(true);
    setError('');

    try {
      await onPhoneAuthVerify(parsePhone(phoneNumber), code, () => {
        history.replace(next);
      });
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPinCodeResend = async ({
    phone_number: phoneNumber,
    force_allow_action: forceAllowAction,
  }) => {
    try {
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(phoneNumber, true, token, forceAllowAction);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const checkEmailExists = async emailAddress => {
    try {
      const { data } = await client.query({
        query: accountEmailExists,
        variables: {
          email_address: emailAddress,
        },
        fetchPolicy: 'network-only',
      });

      return data.accountEmailExists.exists;
    } catch (err) {
      setError(getErrorString(err));
      return false;
    }
  };

  const attemptEmailAuthenticationRequest = async ({ email: emailAddress }) => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await onEmailAuthenticationRequest({ emailAddress });
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptEmailPinVerification = async ({ email: emailAddress, code }) => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const {
        data: { accountLoginByEmail: accountLoginByEmailResponse } = {},
      } = await onLoginByEmail({ emailAddress, code });

      if (accountLoginByEmailResponse.success) {
        dispatch(loginSuccess());
        history.replace(next);
      } else {
        throw new Error('Could not verify user authenticity');
      }
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async values => {
    if (step === AUTH_STEPS.EMAIL) {
      setLoading(true);
      try {
        const emailExists = await checkEmailExists(values.email);

        if (emailExists) {
          // Existing user - show password form
          setStep(AUTH_STEPS.EMAIL_PASSWORD);
        } else {
          // New user - send PIN verification
          await attemptEmailAuthenticationRequest(values);
          setStep(AUTH_STEPS.EMAIL_PIN_VERIFY);
        }
      } catch (err) {
        setError(getErrorString(err));
      } finally {
        setLoading(false);
      }
    } else if (step === AUTH_STEPS.EMAIL_PASSWORD) {
      attemptEmailPasswordLogin(values);
    } else if (step === AUTH_STEPS.PHONE) {
      attemptPhoneVerificationRequest(values);
    } else if (step === AUTH_STEPS.PIN_VERIFY) {
      attemptPhoneLogin(values);
    } else if (step === AUTH_STEPS.EMAIL_PIN_VERIFY) {
      attemptEmailPinVerification(values);
    }
  };

  return (
    <OnboardingLayout>
      <Helmet>
        <title>Welcome to Waldo Photos &ndash; Waldo</title>
      </Helmet>
      {step === AUTH_STEPS.PIN_VERIFY && (
        <Button
          btnStyle="link"
          className="d-flex align-items-center align-self-start text-extra-bold mb-4 ml-3 mt-2 onboarding-back-btn"
          onClick={() => setStep(AUTH_STEPS.PHONE)}
        >
          <Angle direction="left" className="navigation-back-icon" />
        </Button>
      )}
      {[AUTH_STEPS.EMAIL_PASSWORD, AUTH_STEPS.EMAIL_PIN_VERIFY].includes(
        step,
      ) && (
        <Button
          btnStyle="link"
          className="d-flex align-items-center
          align-self-start text-extra-bold mb-4 ml-3 mt-2 onboarding-back-btn"
          onClick={() => setStep(AUTH_STEPS.EMAIL)}
        >
          <Angle direction="left" className="navigation-back-icon" />
        </Button>
      )}
      <WaldoSpeechBubble className="text-center mb-4">
        {step === AUTH_STEPS.PIN_VERIFY ? (
          'A PIN code has been sent to your phone!'
        ) : (
          <p className="px-4">
            Let&apos;s find and deliver you photos from {albumFolder?.name}
          </p>
        )}
      </WaldoSpeechBubble>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          email: '',
          password: '',
          phone_number: '+1',
          code: '',
          force_allow_action: false,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        render={({ values, handleSubmit, handleChange, handleBlur }) => {
          return (
            <>
              {step === AUTH_STEPS.EMAIL && (
                <EmailForm
                  values={values}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  loading={loading}
                  onLoginViaPhone={() => setStep(AUTH_STEPS.PHONE)}
                />
              )}

              {step === AUTH_STEPS.PHONE && (
                <PhoneForm
                  loading={loading || phoneAuthLoading}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  values={values}
                  requireHumanInput={requireHumanInput}
                  blockLogin={blockLogin}
                  onLoginViaEmail={() => setStep(AUTH_STEPS.EMAIL)}
                />
              )}

              {step === AUTH_STEPS.PIN_VERIFY && (
                <PinVerifyForm
                  values={values}
                  submitting={loading || phoneAuthLoading}
                  handleChange={handleChange}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  onPinCodeResend={attemptPinCodeResend}
                  onBack={() => setStep(AUTH_STEPS.PHONE)}
                  requireHumanInput={requireHumanInput}
                  blockLogin={blockLogin}
                />
              )}
              {step === AUTH_STEPS.EMAIL_PASSWORD && (
                <EmailPasswordForm
                  values={values}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  loading={loading}
                  onLoginViaPhone={() => setStep(AUTH_STEPS.PHONE)}
                  onLoginViaPin={() => setStep(AUTH_STEPS.EMAIL_PIN_VERIFY)}
                  onBack={() => setStep(AUTH_STEPS.EMAIL)}
                />
              )}
              {step === AUTH_STEPS.EMAIL_PIN_VERIFY && (
                <EmailPinVerifyForm
                  values={values}
                  submitting={loading}
                  handleChange={handleChange}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  onPinCodeResend={() =>
                    attemptEmailAuthenticationRequest(values)
                  }
                  onBack={() => setStep(AUTH_STEPS.EMAIL)}
                />
              )}
              {error && (
                <div className="w-100 mx-md-6 px-md-6 mt-2">
                  <Alert type="danger" className="w-100 text-center">
                    {error}
                  </Alert>
                </div>
              )}
            </>
          );
        }}
      />
    </OnboardingLayout>
  );
};

const withAlbumFolder = graphql(AlbumFolderWithCreatorBrandDetails, {
  options: ({ match, location }) => {
    const { albumFolderId } = qs.parse(location?.search);

    return {
      variables: {
        id: match.params.albumFolderId || albumFolderId,
      },
    };
  },
  props: ({ data: { albumFolder, loading } }) => ({
    loading,
    albumFolder,
  }),
});

const withLoginMutation = graphql(accountLoginMutation, {
  props: ({ mutate }) => ({
    accountLogin: (email, password) =>
      mutate({
        variables: {
          input: { email, password },
        },
      }),
  }),
});

PhoneAuthView.propTypes = propTypes;

export default compose(
  withRouter,
  withGoogleRecaptchaProvider,
  withAlbumFolder,
  withLoginMutation,
)(PhoneAuthView);
